/* eslint-disable sonarjs/cognitive-complexity */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  RACButton,
  RACModalCard,
  CircularProgress,
  Typography,
  Grid,
  RACCOLOR,
  makeStyles,
} from '@rentacenter/racstrap';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as Expandicon } from '../../assets/images/fullscreen-mode.svg';
import { ReactComponent as InfoIcon } from '../../assets/images/No-records.svg';
import { ReactComponent as DeleteRedIcon } from '../../assets/images/deleteRed-icon.svg';
import { ReactComponent as CancelIcon } from '../../assets/images/alert-cancel-icon.svg';
import { ReactComponent as TickIcon } from '../../assets/images/success-icon.svg';
import { ReactComponent as CloudUploadIcon } from '../../assets/images/cloud-uploading.svg';
import warningIcon from '../../assets/images/warning.png';
import jsPDF from 'jspdf';
import moment from 'moment';
import imageCompression from 'browser-image-compression';
import {
  getLocalSourcingTransactions,
  uploadLocalSourcingReceipt,
} from '../../api/user';
import { FeatureFlagContext } from '../../context/featureFlagContext';
import { fieldDeviceStyles } from '../fieldDeviceStyles';
import {
  LocalSourcingApiResponse,
  LocalSourcingRow,
} from '../../types/localSourcing';

export interface LocalSourcingComponentProps {
  active: boolean;
  onCountChange?: (count: number) => void;
  onErrorShow?: (messageKey?: string) => void;
}

const TEXT_ALIGN_CENTER = 'center';
const BOX_SIZING_BORDER = 'border-box';
const FONT_OPENSANS_REGULAR = 'OpenSans-regular';
const FONT_OPENSANS_BOLD = 'OpenSans-bold';
const PILL_BG_BLUE = '#E9F0FF';
const PILL_HOVER_BLUE = '#DCE7FF';
const BORDER_LIGHT = `1px solid #E5E7EB`;
const DISPLAY_INLINE_BLOCK = 'inline-block';
const DISPLAY_INLINE_FLEX = 'inline-flex';
const FLEX_START = 'flex-start';
const RECEIPT_MAX_BYTES = 5 * 1024 * 1024;
// Lambda sync payload cap is 6 MB; leave a small headroom for JSON envelope.
const RECEIPT_BASE64_MAX_BYTES = Math.floor(5.9 * 1024 * 1024);
const MIME_JPEG = 'image/jpeg';
const MIME_PDF = 'application/pdf';

const readBlobAsDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve((reader.result as string) || '');
    reader.onerror = () =>
      reject(reader.error || new Error('File read failed'));
    reader.readAsDataURL(blob);
  });

const measureBase64Bytes = async (blob: Blob): Promise<number> => {
  const dataUrl = await readBlobAsDataUrl(blob);
  return (dataUrl.split(',')[1] || '').length;
};

const toPdfFile = (blob: Blob, sourceName: string): File => {
  const base = (sourceName || 'receipt').replace(/\.[^./\\]+$/, '');
  return new File([blob], `${base}.pdf`, {
    type: MIME_PDF,
    lastModified: Date.now(),
  });
};

const withTimeout = <T,>(
  p: Promise<T>,
  ms: number,
  label: string
): Promise<T> =>
  Promise.race<T>([
    p,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timeout after ${ms}ms`)), ms)
    ),
  ]);

// Rasterizes each PDF page at decreasing scale until the base64 output fits under maxBase64Bytes
const compressPdf = async (
  pdfBlob: Blob,
  maxBase64Bytes: number
): Promise<Blob> => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
    const pdfjsLib = require('pdfjs-dist/build/pdf');
    pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

    const arrayBuffer = await pdfBlob.arrayBuffer();
    const pdf: any = await withTimeout(
      pdfjsLib.getDocument({ data: arrayBuffer }).promise,
      20000,
      'pdfjs.getDocument'
    );

    // Very long documents can never fit the size cap; bail so the caller
    // rejects with the size-limit error.
    const numPages = pdf.numPages;
    if (numPages > 200) {
      return pdfBlob;
    }
    let startScale = 1.0;
    if (numPages > 100) startScale = 0.4;
    else if (numPages > 30) startScale = 0.6;
    else if (numPages > 10) startScale = 0.8;

    for (
      let scale = startScale;
      scale >= 0.2;
      scale = parseFloat((scale - 0.1).toFixed(1))
    ) {
      const outputPdf = new jsPDF();
      let firstPage = true;
      let scaleFailed = false;
      let overBudget = false;

      // Raw JPEG budget with 15% headroom for PDF container overhead.
      const rawBudgetBytes = Math.floor(((maxBase64Bytes * 3) / 4) * 0.85);
      let approxRawBytes = 0;

      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) return pdfBlob;

          // eslint-disable-next-line no-await-in-loop
          await withTimeout(
            page.render({ canvasContext: ctx, viewport }).promise,
            15000,
            `page.render(page=${pageNum}, scale=${scale})`
          );

          const imgData = canvas.toDataURL(MIME_JPEG, 0.75);
          const b64 = imgData.split(',')[1] || '';
          approxRawBytes += Math.floor((b64.length * 3) / 4);

          const pdfWidth = outputPdf.internal.pageSize.getWidth();
          const pdfHeight = (viewport.height * pdfWidth) / viewport.width;

          if (!firstPage) outputPdf.addPage();
          outputPdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
          firstPage = false;

          if (approxRawBytes > rawBudgetBytes) {
            overBudget = true;
            break;
          }
        } catch {
          scaleFailed = true;
          break;
        }
      }

      if (scaleFailed || overBudget) continue;

      const compressedBlob: Blob = outputPdf.output('blob');
      // eslint-disable-next-line no-await-in-loop
      const base64Size = await measureBase64Bytes(compressedBlob);
      if (base64Size <= maxBase64Bytes) return compressedBlob;
    }
    return pdfBlob;
  } catch {
    return pdfBlob;
  }
};

// Wraps an image in a PDF preserving aspect ratio so portrait receipts
// aren't squashed.
const convertImageFileToPdfBlob = async (file: File): Promise<Blob> => {
  const dataUrl = await readBlobAsDataUrl(file);
  const { width: imgW, height: imgH } = await new Promise<{
    width: number;
    height: number;
  }>((resolve, reject) => {
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth || 1, height: img.naturalHeight || 1 });
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = dataUrl;
  });

  const pdf = new jsPDF();
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 10;
  const maxW = pageW - margin * 2;
  const maxH = pageH - margin * 2;
  const ratio = Math.min(maxW / imgW, maxH / imgH);
  const drawW = imgW * ratio;
  const drawH = imgH * ratio;
  const offsetX = (pageW - drawW) / 2;
  const offsetY = (pageH - drawH) / 2;
  pdf.addImage(dataUrl, 'JPEG', offsetX, offsetY, drawW, drawH);
  return pdf.output('blob');
};

const prepareReceiptForUpload = async (file: File): Promise<File> => {
  const isPdf = file.type === MIME_PDF || /\.pdf$/i.test(file.name);

  if (isPdf) {
    // Upload the original untouched when it already fits.
    try {
      if ((await measureBase64Bytes(file)) <= RECEIPT_BASE64_MAX_BYTES) {
        return file;
      }
    } catch {
      /* fall through */
    }
    const compressed = await compressPdf(file, RECEIPT_BASE64_MAX_BYTES);
    return toPdfFile(compressed, file.name);
  }

  // Image: pre-compress toward the base64 cap, wrap in a PDF, then rasterize
  // if the wrap still overflows.
  const targetMB = ((RECEIPT_BASE64_MAX_BYTES * 3) / 4 / (1024 * 1024)) * 0.85;
  let workingImage: File = file;
  try {
    const compressed = await imageCompression(file, {
      maxSizeMB: targetMB,
      useWebWorker: true,
      initialQuality: 0.92,
    });
    if (compressed && compressed.size > 0) {
      workingImage = new File([compressed], file.name, {
        type: compressed.type || MIME_JPEG,
        lastModified: Date.now(),
      });
    }
  } catch {
    /* keep original */
  }

  const wrappedPdf = await convertImageFileToPdfBlob(workingImage);
  if ((await measureBase64Bytes(wrappedPdf)) <= RECEIPT_BASE64_MAX_BYTES) {
    return toPdfFile(wrappedPdf, file.name);
  }
  const finalPdf = await compressPdf(wrappedPdf, RECEIPT_BASE64_MAX_BYTES);
  return toPdfFile(finalPdf, file.name);
};

const useClasses = makeStyles((theme: any) => ({
  w100: { width: '100%' },
  textCenter: { textAlign: TEXT_ALIGN_CENTER as 'center' },
  textcenter: { textAlign: TEXT_ALIGN_CENTER as 'center' },
  textcenterImage: { textAlign: TEXT_ALIGN_CENTER as 'center' },
  textSpaceNorecords: {
    textAlign: TEXT_ALIGN_CENTER as 'center',
    width: '100%',
  },
  textcenterNorecords: {
    textAlign: TEXT_ALIGN_CENTER as 'center',
    marginTop: theme.typography.pxToRem(60),
    width: '100%',
    fontFamily: FONT_OPENSANS_REGULAR,
    fontSize: theme.typography.pxToRem(14),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  masterLoader: {
    position: 'fixed',
    backgroundColor: `${RACCOLOR.WASHED_BLACK}`,
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    opacity: 0.6,
    zIndex: 2000,
    textAlign: TEXT_ALIGN_CENTER as 'center',
    margin: 0,
  },
  fullScreenLoader: {
    display: 'block',
    position: 'fixed',
    zIndex: 2001,
    top: '50%',
    right: '50%',
  },
  RACloaderNorecords: {
    textAlign: TEXT_ALIGN_CENTER as 'center',
    marginTop: theme.typography.pxToRem(75),
    fontSize: theme.typography.pxToRem(16),
    color: RACCOLOR.RICH_BLACK,
    fontWeight: theme.typography.pxToRem(580),
    marginLeft: theme.typography.pxToRem(30),
    marginRight: theme.typography.pxToRem(30),
    fontFamily: FONT_OPENSANS_REGULAR,
  },
  listgroupitem: {
    width: '100%!important',
    padding: theme.typography.pxToRem(2),
  },
  dinlineblock: { display: DISPLAY_INLINE_BLOCK },
  transactionSpacing: {
    alignItems: 'center',
    marginBottom: '5px',
    padding: '5px',
    '&:hover': { backgroundColor: '#f1f1f1' },
  },
  RACwidgetuser: {
    fontFamily: 'OpenSans-semibold',
    fontSize: theme.typography.pxToRem(14),
    color: RACCOLOR.RICH_BLACK,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    paddingRight: theme.typography.pxToRem(12),
  },
  spaceNowrap: { whiteSpace: 'nowrap' },
  fixedheadertableonline: {
    overflowY: 'auto',
    maxHeight: theme.typography.pxToRem(235),
    '& > .infinite-scroll-component__outerdiv': {
      width: '100%',
      flex: '1 1 100%',
    },
  },
  RACexpandIcon: {
    width: theme.typography.pxToRem(22),
    height: theme.typography.pxToRem(22),
    right: theme.typography.pxToRem(4),
    top: theme.typography.pxToRem(5),
    position: 'absolute',
    cursor: 'pointer',
  },
  RACexpandIconclose: {
    width: theme.typography.pxToRem(22),
    height: theme.typography.pxToRem(22),
    right: theme.typography.pxToRem(4),
    top: theme.typography.pxToRem(5),
    position: 'absolute',
    cursor: 'pointer',
  },
  localSourcingFullModal: {
    '& .MuiDialogTitle-root': {
      paddingTop: theme.typography.pxToRem(8),
      paddingBottom: theme.typography.pxToRem(4),
      marginTop: theme.typography.pxToRem(-6),
    },
    '& .MuiDialog-paper': {
      boxSizing: 'border-box',
      maxWidth: 'calc(100vw - 48px)',
      width: '100%',
    },
  },
  fixedheadertableLocalSourcingPopup: {
    backgroundColor: RACCOLOR.WHITE,
    boxSizing: BOX_SIZING_BORDER as 'border-box',
    overflow: 'hidden',
    height: 'calc(100vh - 190px)',
    maxHeight: 'calc(100vh - 190px)',
    padding: theme.typography.pxToRem(12),
    width: '100%',
    alignContent: FLEX_START,
    alignItems: FLEX_START,
    [theme.breakpoints.down('sm')]: {
      height: 'calc(100vh - 140px)',
      maxHeight: 'calc(100vh - 140px)',
      padding: theme.typography.pxToRem(8),
    },
  },
  localSourcingPopupToolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    backgroundColor: RACCOLOR.WHITE,
    boxSizing: BOX_SIZING_BORDER as 'border-box',
    marginBottom: theme.typography.pxToRem(14),
    paddingRight: theme.typography.pxToRem(28),
    width: '100%',
    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap',
      justifyContent: 'flex-start',
      paddingRight: theme.typography.pxToRem(12),
      rowGap: theme.typography.pxToRem(8),
    },
  },
  localSourcingSearch: {
    border: '1px solid #DDE3EA',
    borderRadius: theme.typography.pxToRem(8),
    height: theme.typography.pxToRem(36),
    minWidth: theme.typography.pxToRem(320),
    paddingLeft: theme.typography.pxToRem(12),
    paddingRight: theme.typography.pxToRem(32),
    fontFamily: FONT_OPENSANS_REGULAR,
    fontSize: theme.typography.pxToRem(12),
    marginRight: theme.typography.pxToRem(8),
    width: '100%',
    [theme.breakpoints.down('md')]: {
      minWidth: theme.typography.pxToRem(200),
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: 0,
      marginRight: 0,
    },
  },
  localSourcingSearchWrapper: {
    position: 'relative',
    display: DISPLAY_INLINE_FLEX,
    alignItems: 'center',
  },
  localSourcingSearchClear: {
    position: 'absolute',
    right: theme.typography.pxToRem(16),
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    width: theme.typography.pxToRem(14),
    height: theme.typography.pxToRem(14),
    background: 'transparent',
    border: 'none',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  localSourcingFilterBtn: {
    backgroundColor: RACCOLOR.DEEP_SKY_BLUE,
    borderRadius: theme.typography.pxToRem(4),
    color: RACCOLOR.WHITE,
    fontSize: theme.typography.pxToRem(12),
    height: theme.typography.pxToRem(36),
    minWidth: theme.typography.pxToRem(72),
    textTransform: 'none',
  },
  localSourcingTable: {
    backgroundColor: RACCOLOR.WHITE,
    boxSizing: BOX_SIZING_BORDER as 'border-box',
    paddingRight: theme.typography.pxToRem(28),
    width: '100%',
    '& .MuiGrid-item': {
      overflow: 'visible !important' as any,
    },
    '& .MuiGrid-item::-webkit-scrollbar, & .MuiGrid-item::-webkit-scrollbar-thumb, & .MuiGrid-item::-webkit-scrollbar-track, & .MuiGrid-item::-webkit-scrollbar-corner':
      {
        display: 'none',
        width: 0,
        height: 0,
        background: 'transparent',
        border: 'none',
      },
  },
  localSourcingTableHeader: {
    alignItems: 'center',
    backgroundColor: RACCOLOR.WHITE,
    boxSizing: BOX_SIZING_BORDER as 'border-box',
    color: RACCOLOR.ONYX,
    fontFamily: FONT_OPENSANS_BOLD,
    fontSize: theme.typography.pxToRem(12),
    padding: `${theme.typography.pxToRem(8)} ${theme.typography.pxToRem(20)}`,
    width: '100%',
    position: 'sticky',
    top: 0,
    zIndex: 2,
    borderBottom: BORDER_LIGHT,
    [theme.breakpoints.down('md')]: {
      padding: `${theme.typography.pxToRem(6)} ${theme.typography.pxToRem(10)}`,
      rowGap: theme.typography.pxToRem(4),
    },
  },
  localSourcingTableRow: {
    alignItems: 'center',
    backgroundColor: RACCOLOR.WHITE,
    border: BORDER_LIGHT,
    borderRadius: theme.typography.pxToRem(4),
    boxSizing: BOX_SIZING_BORDER as 'border-box',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.14)',
    marginBottom: theme.typography.pxToRem(8),
    minHeight: theme.typography.pxToRem(56),
    padding: `${theme.typography.pxToRem(8)} ${theme.typography.pxToRem(20)}`,
    width: '100%',
    [theme.breakpoints.down('md')]: {
      padding: `${theme.typography.pxToRem(8)} ${theme.typography.pxToRem(10)}`,
      rowGap: theme.typography.pxToRem(6),
    },
  },
  localSourcingTableCell: {
    color: '#213547',
    fontFamily: FONT_OPENSANS_REGULAR,
    fontSize: theme.typography.pxToRem(12),
  },
  localSourcingAgreementLink: {
    color: RACCOLOR.DEEP_SKY_BLUE,
    cursor: 'pointer',
    fontFamily: 'OpenSans-semibold',
    fontSize: theme.typography.pxToRem(12),
  },
  localSourcingItemsCell: {
    color: RACCOLOR.ONYX,
    fontFamily: FONT_OPENSANS_REGULAR,
    fontSize: theme.typography.pxToRem(12),
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    paddingRight: theme.typography.pxToRem(12),
  },
  localSourcingPendingReceipt: {
    backgroundColor: PILL_BG_BLUE,
    borderRadius: theme.typography.pxToRem(50),
    color: RACCOLOR.BLUE_CRAYOLA,
    minWidth: theme.typography.pxToRem(180),
    fontSize: theme.typography.pxToRem(13),
    paddingTop: theme.typography.pxToRem(8),
    paddingRight: theme.typography.pxToRem(15),
    paddingBottom: theme.typography.pxToRem(5),
    paddingLeft: theme.typography.pxToRem(15),
    textTransform: 'uppercase',
    fontFamily: FONT_OPENSANS_BOLD,
    '&:hover': { backgroundColor: PILL_HOVER_BLUE },
    [theme.breakpoints.down('md')]: {
      minWidth: theme.typography.pxToRem(120),
      fontSize: theme.typography.pxToRem(11),
    },
  },
  localSourcingCreateAgreement: {
    backgroundColor: PILL_BG_BLUE,
    borderRadius: theme.typography.pxToRem(50),
    color: RACCOLOR.BLUE_CRAYOLA,
    minWidth: theme.typography.pxToRem(180),
    fontSize: theme.typography.pxToRem(13),
    paddingTop: theme.typography.pxToRem(8),
    paddingRight: theme.typography.pxToRem(15),
    paddingBottom: theme.typography.pxToRem(5),
    paddingLeft: theme.typography.pxToRem(15),
    textTransform: 'uppercase',
    fontFamily: FONT_OPENSANS_BOLD,
    '&:hover': { backgroundColor: PILL_HOVER_BLUE },
    [theme.breakpoints.down('md')]: {
      minWidth: theme.typography.pxToRem(120),
      fontSize: theme.typography.pxToRem(11),
    },
  },
  localSourcingPendingDelivery: {
    backgroundColor: '#E9F8F0',
    borderRadius: theme.typography.pxToRem(50),
    color: '#00A96B',
    minWidth: theme.typography.pxToRem(180),
    fontSize: theme.typography.pxToRem(13),
    paddingTop: theme.typography.pxToRem(8),
    paddingRight: theme.typography.pxToRem(15),
    paddingBottom: theme.typography.pxToRem(5),
    paddingLeft: theme.typography.pxToRem(15),
    textTransform: 'uppercase',
    fontFamily: FONT_OPENSANS_BOLD,
    '&:hover': { backgroundColor: '#DCF3E8' },
    [theme.breakpoints.down('md')]: {
      minWidth: theme.typography.pxToRem(120),
      fontSize: theme.typography.pxToRem(11),
    },
  },
  localSourcingPendingPO: {
    backgroundColor: '#FFF3E3',
    borderRadius: theme.typography.pxToRem(50),
    color: '#E67E22',
    minWidth: theme.typography.pxToRem(180),
    fontSize: theme.typography.pxToRem(13),
    paddingTop: theme.typography.pxToRem(8),
    paddingRight: theme.typography.pxToRem(15),
    paddingBottom: theme.typography.pxToRem(5),
    paddingLeft: theme.typography.pxToRem(15),
    textTransform: 'uppercase',
    fontFamily: FONT_OPENSANS_BOLD,
    '&:hover': { backgroundColor: '#FFE8C7' },
    [theme.breakpoints.down('md')]: {
      minWidth: theme.typography.pxToRem(120),
      fontSize: theme.typography.pxToRem(11),
    },
  },
  localSourcingPendingSignature: {
    backgroundColor: '#FFE1EE',
    borderRadius: theme.typography.pxToRem(50),
    color: '#E84D8A',
    minWidth: theme.typography.pxToRem(180),
    fontSize: theme.typography.pxToRem(13),
    paddingTop: theme.typography.pxToRem(8),
    paddingRight: theme.typography.pxToRem(15),
    paddingBottom: theme.typography.pxToRem(5),
    paddingLeft: theme.typography.pxToRem(15),
    textTransform: 'uppercase',
    fontFamily: FONT_OPENSANS_BOLD,
    '&:hover': { backgroundColor: '#FFD3E5' },
    [theme.breakpoints.down('md')]: {
      minWidth: theme.typography.pxToRem(120),
      fontSize: theme.typography.pxToRem(11),
    },
  },
  localSourcingPendingAction: {
    backgroundColor: '#EEF0F2',
    borderRadius: theme.typography.pxToRem(50),
    color: '#68717A',
    minWidth: theme.typography.pxToRem(180),
    fontSize: theme.typography.pxToRem(13),
    paddingTop: theme.typography.pxToRem(8),
    paddingRight: theme.typography.pxToRem(15),
    paddingBottom: theme.typography.pxToRem(5),
    paddingLeft: theme.typography.pxToRem(15),
    textTransform: 'uppercase',
    fontFamily: FONT_OPENSANS_BOLD,
    '&:hover': { backgroundColor: '#E2E5E8' },
    [theme.breakpoints.down('md')]: {
      minWidth: theme.typography.pxToRem(120),
      fontSize: theme.typography.pxToRem(11),
    },
  },
  // Pending Actions popup
  pendingActionsModal: {
    '& [class*="MuiDialog-paper"]': {
      maxWidth: theme.typography.pxToRem(560),
      width: '100%',
    },
    '& [class*="MuiDialogTitle-root"]': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: FLEX_START,
      gap: theme.typography.pxToRem(10),
      borderBottom: BORDER_LIGHT,
      paddingTop: theme.typography.pxToRem(14),
      paddingBottom: theme.typography.pxToRem(12),
      marginBottom: 0,
      '& > *': {
        margin: 0,
        flex: '0 0 auto',
      },
      '& [class*="MuiTypography-root"]': {
        fontSize: theme.typography.pxToRem(16),
        fontFamily: FONT_OPENSANS_BOLD,
        color: '#111827',
        lineHeight: 1.2,
      },
      '& [class*="MuiIconButton-root"]': {
        marginLeft: 'auto',
        padding: theme.typography.pxToRem(4),
      },
      '&::before': {
        content: '""',
        display: DISPLAY_INLINE_BLOCK,
        flex: '0 0 auto',
        width: theme.typography.pxToRem(24),
        height: theme.typography.pxToRem(24),
        backgroundImage: `url(${warningIcon})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'contain',
        backgroundPosition: 'center',
      },
    },
  },
  uploadReceiptModal: {
    '& [class*="MuiDialog-paper"]': {
      maxWidth: theme.typography.pxToRem(460),
      width: '100%',
    },
    '& [class*="MuiDialogTitle-root"]': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: FLEX_START,
      gap: theme.typography.pxToRem(10),
      borderBottom: BORDER_LIGHT,
      paddingTop: theme.typography.pxToRem(14),
      paddingBottom: theme.typography.pxToRem(12),
      marginBottom: 0,
      '& > *': {
        margin: 0,
        flex: '0 0 auto',
      },
      '& [class*="MuiTypography-root"]': {
        fontSize: theme.typography.pxToRem(16),
        fontFamily: FONT_OPENSANS_BOLD,
        color: '#111827',
        lineHeight: 1.2,
      },
      '& [class*="MuiIconButton-root"]': {
        marginLeft: 'auto',
        padding: theme.typography.pxToRem(4),
      },
      '&::before': {
        content: '""',
        display: DISPLAY_INLINE_BLOCK,
        flex: '0 0 auto',
        width: theme.typography.pxToRem(24),
        height: theme.typography.pxToRem(24),
        backgroundImage: `url(${warningIcon})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'contain',
        backgroundPosition: 'center',
      },
    },
  },
  pendingActionsBody: {
    paddingTop: theme.typography.pxToRem(20),
  },
  uploadReceiptBody: {
    paddingTop: theme.typography.pxToRem(20),
  },
  pendingActionsBanner: {
    background: '#F5F5F5',
    border: BORDER_LIGHT,
    borderLeft: '4px solid #F5A524',
    borderRadius: theme.typography.pxToRem(6),
    padding: `${theme.typography.pxToRem(12)} ${theme.typography.pxToRem(14)}`,
    fontSize: theme.typography.pxToRem(13),
    lineHeight: 1.5,
    color: '#3F3F46',
    fontFamily: FONT_OPENSANS_REGULAR,
    marginBottom: theme.typography.pxToRem(20),
  },
  pendingActionsRadioList: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.typography.pxToRem(28),
    padding: `${theme.typography.pxToRem(4)} ${theme.typography.pxToRem(4)}`,
    marginBottom: theme.typography.pxToRem(24),
  },
  pendingActionsRadioCard: {
    display: DISPLAY_INLINE_FLEX,
    alignItems: 'center',
    gap: theme.typography.pxToRem(8),
    cursor: 'pointer',
    fontFamily: FONT_OPENSANS_REGULAR,
    fontSize: theme.typography.pxToRem(14),
    color: '#111827',
  },
  pendingActionsRadioCardSelected: {},
  pendingActionsRadioInput: {
    accentColor: RACCOLOR.BLUE_CRAYOLA,
    width: theme.typography.pxToRem(16),
    height: theme.typography.pxToRem(16),
    margin: 0,
    cursor: 'pointer',
  },
  pendingActionsFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTop: BORDER_LIGHT,
    paddingTop: theme.typography.pxToRem(16),
    marginTop: theme.typography.pxToRem(8),
  },
  pendingActionsCancelBtn: {
    color: '#2179FE',
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    fontFamily: FONT_OPENSANS_REGULAR,
    padding: `${theme.typography.pxToRem(6)} ${theme.typography.pxToRem(12)}`,
    '&:hover': { background: 'transparent', textDecoration: 'underline' },
  },
  localSourcingActionCellRight: {
    textAlign: 'right',
    '& > button': {
      minWidth: 0,
      maxWidth: '100%',
      width: '100%',
      paddingLeft: theme.typography.pxToRem(8),
      paddingRight: theme.typography.pxToRem(8),
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  localSourcingInfiniteFull: {
    width: '100%',
  },
  localSourcingInfiniteVisible: {
    overflow: 'visible !important' as any,
    width: '100%',
  },
  uploadReceiptContainer: {
    padding: '8px 4px',
  },
  uploadReceiptBelowLabel: {
    marginTop: theme.typography.pxToRem(14),
    marginBottom: theme.typography.pxToRem(8),
    textAlign: TEXT_ALIGN_CENTER as 'center',
    fontFamily: FONT_OPENSANS_BOLD,
    fontSize: theme.typography.pxToRem(13),
    color: '#111827',
  },
  receiptDropZone: {
    display: 'block',
    border: '2px dashed #2179FE',
    borderRadius: theme.typography.pxToRem(8),
    padding: '28px 16px',
    textAlign: TEXT_ALIGN_CENTER as 'center',
    cursor: 'pointer',
    background: '#F5F9FF',
  },
  receiptUploadIcon: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.typography.pxToRem(8),
    '& svg': {
      width: theme.typography.pxToRem(36),
      height: 'auto',
    },
  },
  receiptDropTitle: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
    color: '#2179FE',
  },
  receiptDropHint: {
    fontSize: theme.typography.pxToRem(12),
    color: '#6F7F91',
    marginTop: theme.typography.pxToRem(4),
  },
  receiptFileInputHidden: {
    display: 'none',
  },
  receiptError: {
    marginTop: theme.typography.pxToRem(12),
    padding: '10px 12px',
    border: '1px solid #F5C2C7',
    borderRadius: theme.typography.pxToRem(6),
    background: '#FFF1F2',
    color: '#B02A37',
    fontSize: theme.typography.pxToRem(13),
  },
  receiptFileRow: {
    marginTop: theme.typography.pxToRem(12),
    padding: '10px 12px',
    border: '1px solid #E1E6EB',
    borderRadius: theme.typography.pxToRem(6),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#FFFFFF',
  },
  receiptFileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.typography.pxToRem(10),
  },
  receiptFileTypeBadge: {
    display: DISPLAY_INLINE_BLOCK,
    width: theme.typography.pxToRem(28),
    height: theme.typography.pxToRem(28),
    background: '#FFEDED',
    color: '#D32F2F',
    borderRadius: theme.typography.pxToRem(4),
    textAlign: TEXT_ALIGN_CENTER as 'center',
    lineHeight: theme.typography.pxToRem(28),
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 700,
  },
  receiptFileName: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 600,
  },
  receiptFileMeta: {
    fontSize: theme.typography.pxToRem(11),
    color: '#6F7F91',
    display: 'flex',
    alignItems: 'center',
    gap: theme.typography.pxToRem(6),
    marginTop: theme.typography.pxToRem(4),
  },
  receiptFileMetaDot: {
    display: DISPLAY_INLINE_BLOCK,
    width: theme.typography.pxToRem(3),
    height: theme.typography.pxToRem(3),
    borderRadius: '50%',
    background: '#6F7F91',
  },
  receiptFileCompleted: {
    display: DISPLAY_INLINE_FLEX,
    alignItems: 'center',
    gap: theme.typography.pxToRem(4),
    color: '#2E7D32',
    fontWeight: 600,
  },
  receiptFileCompletedIcon: {
    display: DISPLAY_INLINE_FLEX,
    alignItems: 'center',
    justifyContent: 'center',
    width: theme.typography.pxToRem(14),
    height: theme.typography.pxToRem(14),
    borderRadius: '50%',
    background: '#2E7D32',
    color: RACCOLOR.WHITE,
    fontSize: theme.typography.pxToRem(10),
    lineHeight: 1,
    fontWeight: 700,
  },
  receiptDeleteButton: {
    cursor: 'pointer',
    display: DISPLAY_INLINE_FLEX,
    alignItems: 'center',
  },
  uploadReceiptSpinner: {
    color: RACCOLOR.WHITE,
  },
  receiptSuccessBody: {
    padding: `${theme.typography.pxToRem(8)} ${theme.typography.pxToRem(4)}`,
    textAlign: TEXT_ALIGN_CENTER as 'center',
  },
  receiptSuccessMessage: {
    fontFamily: FONT_OPENSANS_BOLD,
    fontSize: theme.typography.pxToRem(16),
    letterSpacing: 0,
    marginTop: theme.typography.pxToRem(15),
    marginBottom: theme.typography.pxToRem(25),
  },
}));

const LOCAL_SOURCING_PAGE_SIZE = 20;

// AC-driven action codes returned by BE in obj.actions[]
const LS_ACTION_LABELS: Record<string, string> = {
  CREATE_MPO: 'lsActionPendingPO',
  CREATE_AGREEMENT: 'lsActionCreateAgreement',
  UPLOAD_RECEIPT: 'lsActionPendingReceipt',
  TAKE_PAYMENT: 'lsActionTakePayment',
};

// Popup-only label overrides (Pending Actions radio choices)
const getPendingActionPopupLabel = (act: string): string => {
  if (act === 'CREATE_AGREEMENT') return 'lsActionCreateAgreementPopup';
  if (act === 'UPLOAD_RECEIPT') return 'lsActionUploadReceiptPopup';
  return LS_ACTION_LABELS[act] || act;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const LocalSourcingComponent: React.FC<LocalSourcingComponentProps> = ({
  active,
  onCountChange,
  onErrorShow,
}) => {
  const Btn: any = RACButton;
  const history = useHistory();
  const { t } = useTranslation('dashboard');
  const classes = useClasses();
  const classes1 = fieldDeviceStyles();

  const { localSourceRamp } = useContext(FeatureFlagContext);
  const enabled = localSourceRamp == '1';

  const [localSourcingData, setLocalSourcingData] = useState<
    LocalSourcingRow[]
  >([]);
  const [localSourcingLoader, setLocalSourcingLoader] = useState(enabled);
  const [localSourcingPop, setLocalSourcingPop] = useState(false);
  const [localSourcingSearchText, setLocalSourcingSearchText] = useState('');
  const [selectedLocalSourcingTask, setSelectedLocalSourcingTask] =
    useState<any>(null);
  // AC-driven popups (Upload Receipt / Pending Actions)
  const [uploadReceiptPopupOpen, setUploadReceiptPopupOpen] = useState(false);
  const [pendingActionsPopupOpen, setPendingActionsPopupOpen] = useState(false);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptFileError, setReceiptFileError] = useState<string>('');
  const [uploadReceiptLoader, setUploadReceiptLoader] = useState(false);
  const [receiptSuccessOpen, setReceiptSuccessOpen] = useState(false);
  const [pendingActionsChoice, setPendingActionsChoice] = useState<string>('');
  // Confirmation popup for CREATE_AGREEMENT / CREATE_MPO / TAKE_PAYMENT
  const [confirmActionOpen, setConfirmActionOpen] = useState<string | null>(
    null
  );
  // Pagination
  const [localSourcingHasMore, setLocalSourcingHasMore] = useState(true);
  const [localSourcingFetchLoader, setLocalSourcingFetchLoader] =
    useState(false);
  const [localSourcingPreloadOverlay, setLocalSourcingPreloadOverlay] =
    useState(false);
  const [localSourcingTotalResults, setLocalSourcingTotalResults] = useState(0);
  const [localSourcingNoMore, setLocalSourcingNoMore] = useState(false);
  // Note: backend errors are intentionally NOT surfaced in the UI.
  const localSourcingPageRef = useRef(1);
  const localSourcingInFlightRef = useRef(false);
  const localSourcingSeenIdsRef = useRef<Set<string>>(new Set());
  const localSourcingRequestGenerationRef = useRef(0);
  const [localSourcingRefreshTick, setLocalSourcingRefreshTick] = useState(0);
  const refreshLocalSourcingData = () => {
    localSourcingRequestGenerationRef.current += 1;
    setLocalSourcingRefreshTick((tick) => tick + 1);
  };
  const warnLocalSourcing = (
    msg: string,
    ctx?: Record<string, unknown>
  ): void => {
    // eslint-disable-next-line no-console
    console.warn(`[localSourcing] ${msg}`, ctx ?? {});
  };

  // Notify parent of row count for tab label
  useEffect(() => {
    if (onCountChange) {
      onCountChange(localSourcingTotalResults || localSourcingData.length);
    }
  }, [localSourcingData.length, localSourcingTotalResults, onCountChange]);

  const extractLocalSourcingRows = (
    response: LocalSourcingApiResponse | null | undefined
  ): LocalSourcingRow[] => {
    const data = response?.data;
    if (!data) return [];
    return Array.isArray(data.fundRequests) ? data.fundRequests : [];
  };

  const localSourcingRowKey = (
    r: LocalSourcingRow | null | undefined
  ): string => {
    const frId = String(r?.fundingRequestId ?? '');
    if (frId) return `FR:${frId}`;
    const agr = String(
      (r as { agreementNumber?: unknown })?.agreementNumber ?? ''
    );
    return agr ? `AGR:${agr}` : '';
  };

  useEffect(() => {
    if (!enabled) return;
    const requestGeneration = localSourcingRequestGenerationRef.current;
    const fetchLocalSourcing = async () => {
      setLocalSourcingLoader(true);
      const storeNumber = window.sessionStorage.getItem('storeNumber') ?? '';
      try {
        const response = await getLocalSourcingTransactions(
          storeNumber,
          1,
          LOCAL_SOURCING_PAGE_SIZE
        );
        if (requestGeneration !== localSourcingRequestGenerationRef.current)
          return;
        if (response?.data && response?.status === 200) {
          const rows = extractLocalSourcingRows(response);
          const total =
            Number(response?.data?.totalResults ?? rows.length) || 0;
          localSourcingSeenIdsRef.current = new Set(
            rows.map(localSourcingRowKey).filter((k) => k)
          );
          localSourcingPageRef.current = 1;
          setLocalSourcingData(rows);
          setLocalSourcingTotalResults(total);
          const more = rows.length > 0 && rows.length < total;
          setLocalSourcingHasMore(more);
          setLocalSourcingNoMore(!more);
        } else if (response?.status === 204) {
          localSourcingSeenIdsRef.current = new Set();
          localSourcingPageRef.current = 1;
          setLocalSourcingData([]);
          setLocalSourcingTotalResults(0);
          setLocalSourcingHasMore(false);
          setLocalSourcingNoMore(true);
        } else {
          warnLocalSourcing('initial load returned no data', {
            status: response?.status,
          });
          setLocalSourcingHasMore(false);
          setLocalSourcingNoMore(true);
        }
      } catch (err) {
        if (requestGeneration !== localSourcingRequestGenerationRef.current)
          return;
        warnLocalSourcing('initial load threw', { err });
        setLocalSourcingData([]);
        setLocalSourcingTotalResults(0);
        setLocalSourcingHasMore(false);
        setLocalSourcingNoMore(true);
      } finally {
        if (requestGeneration === localSourcingRequestGenerationRef.current)
          setLocalSourcingLoader(false);
      }
    };

    const w = window as Window & {
      requestIdleCallback?: (
        cb: () => void,
        opts?: { timeout: number }
      ) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    const ric = w.requestIdleCallback;
    const cic = w.cancelIdleCallback;
    const schedule: (cb: () => void) => number = ric
      ? (cb) => ric(cb, { timeout: 1500 })
      : (cb) => window.setTimeout(cb, 0);
    const cancel: (h: number) => void = cic
      ? (h) => cic(h)
      : (h) => window.clearTimeout(h);
    const handle = schedule(() => {
      fetchLocalSourcing();
    });
    return () => {
      localSourcingRequestGenerationRef.current += 1;
      cancel(handle);
    };
  }, [enabled, localSourcingRefreshTick]);

  const dedupeNewRows = (rows: LocalSourcingRow[]): LocalSourcingRow[] => {
    const kept: LocalSourcingRow[] = [];
    const newKeys: string[] = [];
    for (const r of rows) {
      const key = localSourcingRowKey(r);
      if (!key) {
        kept.push(r);
        continue;
      }
      if (localSourcingSeenIdsRef.current.has(key) || newKeys.includes(key))
        continue;
      newKeys.push(key);
      kept.push(r);
    }
    for (const k of newKeys) localSourcingSeenIdsRef.current.add(k);
    return kept;
  };

  const applyNextPageRows = (
    rows: LocalSourcingRow[],
    total: number,
    nextPage: number
  ) => {
    localSourcingPageRef.current = nextPage;
    setLocalSourcingData((prev) => {
      const merged = [...prev, ...rows];
      const more = rows.length > 0 && !!total && merged.length < total;
      setLocalSourcingHasMore(more);
      setLocalSourcingNoMore(!more);
      return merged;
    });
    if (total) setLocalSourcingTotalResults(total);
  };

  const fetchNextLocalSourcing = async () => {
    if (!localSourcingHasMore || localSourcingInFlightRef.current) return;
    localSourcingInFlightRef.current = true;
    const requestGeneration = localSourcingRequestGenerationRef.current;
    const nextPage = localSourcingPageRef.current + 1;
    setLocalSourcingFetchLoader(true);
    const storeNumber = window.sessionStorage.getItem('storeNumber') ?? '';
    try {
      const response = await getLocalSourcingTransactions(
        storeNumber,
        nextPage,
        LOCAL_SOURCING_PAGE_SIZE
      );
      if (requestGeneration !== localSourcingRequestGenerationRef.current)
        return;
      if (response?.data && response?.status === 200) {
        const rows = extractLocalSourcingRows(response);
        const total =
          Number(response?.data?.totalResults ?? localSourcingTotalResults) ||
          0;
        applyNextPageRows(dedupeNewRows(rows), total, nextPage);
      } else if (response?.status === 204) {
        setLocalSourcingHasMore(false);
        setLocalSourcingNoMore(true);
      } else {
        warnLocalSourcing('next page returned no data', {
          page: nextPage,
          status: response?.status,
        });
        setLocalSourcingHasMore(false);
        setLocalSourcingNoMore(true);
      }
    } catch (err) {
      if (requestGeneration !== localSourcingRequestGenerationRef.current)
        return;
      warnLocalSourcing('next page threw', { page: nextPage, err });
      setLocalSourcingHasMore(false);
      setLocalSourcingNoMore(true);
    } finally {
      localSourcingInFlightRef.current = false;
      if (requestGeneration === localSourcingRequestGenerationRef.current) {
        setLocalSourcingFetchLoader(false);
        setLocalSourcingPreloadOverlay(false);
      }
    }
  };

  const getLocalSourcingActions = (obj: any): string[] => {
    return Array.isArray(obj?.actions) ? obj.actions.filter(Boolean) : [];
  };

  const resolveLocalSourcingAction = (
    actions: string[]
  ): 'hidden' | 'single' | 'popup-upload' | 'popup-pending' => {
    if (!actions || actions.length === 0) return 'hidden';
    if (actions.length === 1) return 'single';
    return 'popup-pending';
  };

  const getLocalSourcingPillLabel = (obj: any): string => {
    const actions = getLocalSourcingActions(obj);
    const mode = resolveLocalSourcingAction(actions);
    if (mode === 'hidden') return '';
    if (mode === 'single') return LS_ACTION_LABELS[actions[0]] || actions[0];
    if (mode === 'popup-upload') return LS_ACTION_LABELS.UPLOAD_RECEIPT;
    return 'lsPendingAction';
  };

  const showError = (messageKey?: string) => {
    if (onErrorShow) onErrorShow(messageKey);
  };

  const buildLocalSourcingRedirectState = (obj: any) => ({
    spendLimitId: obj?.spendLimitId,
    storeNumber: obj?.storeNumber,
    vendorName: obj?.vendorName,
    vendorId: obj?.vendorId ?? null,
    supplierName: obj?.supplierName,
    supplierOrderNumber: obj?.supplierOrderNumber,
    items: Array.isArray(obj?.items) ? obj.items : [],
  });

  const performRedirect = (action: string, obj: any) => {
    const customerId = obj?.customerId || obj?.globalCustomerId;
    if (!customerId) {
      showError();
      return;
    }
    const spendLimitId = obj?.spendLimitId || '';
    const spendLimitSearch = spendLimitId
      ? `?spendLimitId=${spendLimitId}`
      : '';
    const redirectState = buildLocalSourcingRedirectState(obj);
    switch (action) {
      case 'CREATE_MPO':
        history.push({
          pathname: `/mpo/create`,
          search: `?dashboard=true`,
          state: redirectState,
        });
        return;
      case 'CREATE_AGREEMENT':
        history.push({
          pathname: `/agreement/customerorder/details/${customerId}${spendLimitSearch}`,
          state: redirectState,
        });
        return;
      case 'TAKE_PAYMENT': {
        const agreementId = obj?.agreementId || '';
        if (!agreementId) {
          showError();
          return;
        }
        history.push({
          pathname: `/payment1/customerorder/preliminary/${customerId}/${agreementId}${spendLimitSearch}`,
          state: redirectState,
        });
        return;
      }
      default:
        showError();
    }
  };

  const redirectForAction = (action: string, obj: any) => {
    const customerId = obj?.customerId || obj?.globalCustomerId;
    if (!customerId) {
      showError();
      return;
    }
    if (action === 'UPLOAD_RECEIPT') {
      setSelectedLocalSourcingTask(obj);
      setReceiptFile(null);
      setReceiptFileError('');
      setUploadReceiptPopupOpen(true);
      return;
    }
    if (
      action === 'CREATE_AGREEMENT' ||
      action === 'CREATE_MPO' ||
      action === 'TAKE_PAYMENT'
    ) {
      setSelectedLocalSourcingTask(obj);
      setConfirmActionOpen(action);
      return;
    }
    showError();
  };

  const closeUploadReceiptPopup = () => {
    if (uploadReceiptLoader) return;
    setUploadReceiptPopupOpen(false);
    setReceiptFile(null);
    setReceiptFileError('');
    setSelectedLocalSourcingTask(null);
  };

  const closePendingActionsPopup = () => {
    setPendingActionsPopupOpen(false);
    setPendingActionsChoice('');
    setSelectedLocalSourcingTask(null);
  };

  const closeConfirmActionPopup = () => {
    setConfirmActionOpen(null);
    setSelectedLocalSourcingTask(null);
  };

  const proceedConfirmAction = () => {
    const action = confirmActionOpen;
    const task = selectedLocalSourcingTask;
    setConfirmActionOpen(null);
    if (action && task) {
      performRedirect(action, task);
    }
    setSelectedLocalSourcingTask(null);
  };

  // Content config for the CREATE_AGREEMENT / CREATE_MPO / TAKE_PAYMENT popup
  const CONFIRM_ACTION_CONTENT: Record<
    string,
    { title: string; banner: string; cta: string }
  > = {
    CREATE_AGREEMENT: {
      title: 'lsConfirmCreateAgreementTitle',
      banner: 'lsConfirmCreateAgreementBanner',
      cta: 'lsConfirmCreateAgreementCta',
    },
    CREATE_MPO: {
      title: 'lsConfirmCreateMpoTitle',
      banner: 'lsConfirmCreateMpoBanner',
      cta: 'lsConfirmCreateMpoCta',
    },
    TAKE_PAYMENT: {
      title: 'lsConfirmTakePaymentTitle',
      banner: 'lsConfirmTakePaymentBanner',
      cta: 'lsConfirmTakePaymentCta',
    },
  };

  const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          resolve(result);
        } else {
          reject(new Error('Unexpected reader result type'));
        }
      };
      reader.onerror = () =>
        reject(reader.error || new Error('File read failed'));
      reader.readAsDataURL(file);
    });

  const submitReceipt = async () => {
    const task = selectedLocalSourcingTask;
    const file = receiptFile;
    const spendLimitId = task?.spendLimitId;
    const poNumber = task?.purchaseOrderNumber;
    if (!file || !spendLimitId || !poNumber) {
      showError();
      return;
    }
    setUploadReceiptLoader(true);
    try {
      const fileToUpload = await withTimeout(
        prepareReceiptForUpload(file),
        60000,
        'prepareReceiptForUpload'
      );
      const receipt = await readFileAsBase64(fileToUpload);
      const base64Bytes = (receipt.split(',')[1] || '').length;
      if (base64Bytes > RECEIPT_BASE64_MAX_BYTES) {
        setReceiptFileError(t('lsReceiptTooManyPages'));
        return;
      }
      const response = await uploadLocalSourcingReceipt({
        receipt,
        spendLimitId,
        poNumber,
      });
      const status = Number(response?.status ?? 0);
      const ok = status >= 200 && status < 300 && !!response?.data;
      if (!ok) {
        showError('Something went Wrong!');
        return;
      }
    } catch {
      showError('Something went Wrong!');
      return;
    } finally {
      setUploadReceiptLoader(false);
    }
    setUploadReceiptPopupOpen(false);
    setReceiptFile(null);
    setReceiptFileError('');
    setReceiptSuccessOpen(true);
  };

  const closeReceiptSuccessPopup = () => {
    setReceiptSuccessOpen(false);
    setSelectedLocalSourcingTask(null);
    refreshLocalSourcingData();
  };

  const proceedPendingAction = () => {
    if (!pendingActionsChoice) return;
    const task = selectedLocalSourcingTask;
    const choice = pendingActionsChoice;
    if (choice === 'UPLOAD_RECEIPT') {
      setPendingActionsPopupOpen(false);
      setPendingActionsChoice('');
      setReceiptFile(null);
      setReceiptFileError('');
      setUploadReceiptPopupOpen(true);
      return;
    }
    closePendingActionsPopup();
    performRedirect(choice, task);
  };

  const getLocalSourcingCustomerName = (obj: any) =>
    `${obj?.firstName || ''} ${obj?.lastName || ''}`.trim();

  const getLocalSourcingCreatedDate = (obj: any) => {
    const raw = obj?.createdDate;
    if (!raw) return '';
    const datePart = String(raw).split('T')[0];
    const formatted = moment(datePart).format('MM/DD/YYYY');
    return formatted === 'Invalid date' ? raw : formatted;
  };

  const getLocalSourcingItems = (obj: any): string => obj?.brandName ?? '';

  const getLocalSourcingStatusClass = (statusKey: string) => {
    switch (statusKey) {
      case 'lsActionCreateAgreement':
      case 'lsActionCreateAgreementPopup':
      case 'lsActionPendingReceipt':
        return classes.localSourcingPendingReceipt;
      case 'lsActionTakePayment':
        return classes.localSourcingPendingDelivery;
      case 'lsActionPendingPO':
        return classes.localSourcingPendingPO;
      case 'lsPendingAction':
      default:
        return classes.localSourcingPendingAction;
    }
  };

  const filteredLocalSourcingData = useMemo(() => {
    const searchText = localSourcingSearchText.toLowerCase().trim();
    if (!searchText) return localSourcingData;
    return localSourcingData.filter((obj: any) => {
      return [
        getLocalSourcingCreatedDate(obj),
        getLocalSourcingCustomerName(obj),
        obj?.agreementNumber,
        getLocalSourcingItems(obj),
        getLocalSourcingPillLabel(obj),
        obj?.vendorName,
        obj?.fundingOrigin,
        obj?.purchaseOrderNumber,
      ].some((value) => `${value || ''}`.toLowerCase().includes(searchText));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSourcingData, localSourcingSearchText]);

  const handleLocalSourcingClick = (obj: any) => {
    const actions = getLocalSourcingActions(obj);
    const mode = resolveLocalSourcingAction(actions);
    if (mode === 'hidden') return;
    if (mode === 'single') {
      redirectForAction(actions[0], obj);
      return;
    }
    if (mode === 'popup-upload') {
      setSelectedLocalSourcingTask(obj);
      setReceiptFile(null);
      setUploadReceiptPopupOpen(true);
      return;
    }
    setSelectedLocalSourcingTask(obj);
    setPendingActionsChoice(actions[0]);
    setPendingActionsPopupOpen(true);
  };

  const buildLocalSourcingGrid = () => {
    if (localSourcingData.length === 0) return null;
    return localSourcingData.map((obj: any, index: number) => {
      const actions = getLocalSourcingActions(obj);
      const showPill = actions.length > 0;
      const pillLabel = getLocalSourcingPillLabel(obj);
      return (
        <Grid key={`local-sourcing-${index}`} className={`${classes.w100}`}>
          <Grid
            className={`${classes.listgroupitem} ${classes1.listgroupitemTab}`}
            aria-current="true"
          >
            <Grid
              container
              spacing={0}
              className={classes.transactionSpacing}
              alignItems="center"
            >
              <Grid item sm={4} md={4} className={classes.dinlineblock}>
                <Typography
                  variant="subtitle2"
                  className={classes.RACwidgetuser}
                  title={getLocalSourcingCustomerName(obj)}
                >
                  {getLocalSourcingCustomerName(obj)}
                </Typography>
              </Grid>
              <Grid
                item
                sm={4}
                md={4}
                className={`${classes.dinlineblock} ${classes.localSourcingItemsCell}`}
                title={getLocalSourcingItems(obj)}
              >
                {getLocalSourcingItems(obj)}
              </Grid>
              <Grid
                item
                sm={4}
                md={4}
                className={`${classes.dinlineblock} ${classes.localSourcingActionCellRight}`}
              >
                {showPill && (
                  <Btn
                    className={`${getLocalSourcingStatusClass(pillLabel)} ${
                      classes1.meTab4
                    }`}
                    onClick={() => handleLocalSourcingClick(obj)}
                  >
                    {t(pillLabel)}
                  </Btn>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    });
  };

  const buildLocalSourcingExpandedGrid = () => {
    const filtered = filteredLocalSourcingData;
    const isSearching = !!localSourcingSearchText.trim();
    const rowsMarkup = filtered.map((obj: any, index: number) => {
      const actions = getLocalSourcingActions(obj);
      const showPill = actions.length > 0;
      const pillLabel = getLocalSourcingPillLabel(obj);
      return (
        <Grid
          container
          spacing={0}
          className={classes.localSourcingTableRow}
          key={`local-sourcing-expanded-${index}`}
        >
          <Grid
            item
            xs={6}
            sm={4}
            md={2}
            className={classes.localSourcingTableCell}
          >
            {getLocalSourcingCreatedDate(obj)}
          </Grid>
          <Grid
            item
            xs={6}
            sm={4}
            md={2}
            className={classes.localSourcingTableCell}
          >
            {getLocalSourcingCustomerName(obj)}
          </Grid>
          <Grid
            item
            xs={6}
            sm={4}
            md={2}
            className={classes.localSourcingAgreementLink}
            onClick={() => handleLocalSourcingClick(obj)}
          >
            {obj.agreementNumber}
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            className={classes.localSourcingItemsCell}
            title={getLocalSourcingItems(obj)}
          >
            {getLocalSourcingItems(obj)}
          </Grid>
          <Grid item xs={6} sm={6} md={2} className={classes.dinlineblock}>
            {showPill && (
              <Btn
                className={`${getLocalSourcingStatusClass(pillLabel)} ${
                  classes1.meTab4
                }`}
                onClick={() => handleLocalSourcingClick(obj)}
              >
                {t(pillLabel)}
              </Btn>
            )}
          </Grid>
        </Grid>
      );
    });

    return (
      <Grid className={classes.localSourcingTable}>
        <Grid
          container
          spacing={0}
          className={classes.localSourcingTableHeader}
        >
          <Grid item xs={6} sm={4} md={2}>
            {t('lsDateCreated')}
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            {t('lsCustomer')}
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            {t('lsAgreementNumber')}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            {t('lsItems')}
          </Grid>
          <Grid item xs={6} sm={6} md={2}>
            {t('lsAction')}
          </Grid>
        </Grid>
        {localSourcingPop && localSourcingPreloadOverlay ? (
          <Grid
            className={`${classes.RACloaderNorecords} ${classes.textcenter} ${classes.w100}`}
            style={{
              height: 'calc(100vh - 300px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Grid>
        ) : (
          <InfiniteScroll
            next={fetchNextLocalSourcing}
            dataLength={localSourcingData.length}
            hasMore={localSourcingHasMore && !isSearching}
            height={'calc(100vh - 300px)'}
            style={{ overflowX: 'hidden' }}
            className={classes.localSourcingInfiniteFull}
            loader={
              localSourcingPop &&
              filtered.length > 0 &&
              localSourcingFetchLoader ? (
                <Grid className={`${classes.textCenter} ${classes.w100}`}>
                  <CircularProgress />
                </Grid>
              ) : localSourcingPop &&
                filtered.length > 0 &&
                localSourcingNoMore ? (
                <Grid className={`${classes.textCenter} ${classes.w100}`}>
                  <Typography>{t('lsNoMoreData')}</Typography>
                </Grid>
              ) : null
            }
          >
            {filtered.length > 0 ? (
              <>
                {rowsMarkup}
                {localSourcingPop && isSearching && localSourcingFetchLoader ? (
                  <Grid className={`${classes.textCenter} ${classes.w100}`}>
                    <CircularProgress />
                  </Grid>
                ) : null}
              </>
            ) : localSourcingPop &&
              (localSourcingFetchLoader ||
                (!isSearching && localSourcingHasMore)) ? (
              <Grid
                className={`${classes.RACloaderNorecords} ${classes.textcenter} ${classes.w100}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '40px 0',
                }}
              >
                <CircularProgress />
              </Grid>
            ) : (
              <Grid className={classes.textSpaceNorecords}>
                <Grid className={classes.textcenterNorecords}>
                  <InfoIcon />
                </Grid>
                <Grid className={classes.textcenterImage}>
                  {t('lsNoRecordsFound')}
                </Grid>
              </Grid>
            )}
          </InfiniteScroll>
        )}
      </Grid>
    );
  };

  const localSourcingPopupDiv = () => (
    <Grid
      container
      spacing={0}
      className={classes.fixedheadertableLocalSourcingPopup}
      key="uniqueLocalSourcing"
    >
      <Expandicon
        className={classes.RACexpandIconclose}
        data-bs-toggle="modal"
        data-bs-target="#localSourcing"
        onClick={() => {
          setLocalSourcingSearchText('');
          setLocalSourcingPop(false);
        }}
        data-testid="expandLocalSourcingClose"
        key="localSourcingGridIcon"
      />
      <Grid item md={12} className={classes.localSourcingPopupToolbar}>
        <span className={classes.localSourcingSearchWrapper}>
          <input
            className={classes.localSourcingSearch}
            placeholder={t('lsSearch')}
            value={localSourcingSearchText}
            onChange={(event) => setLocalSourcingSearchText(event.target.value)}
          />
          {localSourcingSearchText && (
            <button
              type="button"
              className={classes.localSourcingSearchClear}
              onClick={() => setLocalSourcingSearchText('')}
              data-testid="localSourcingSearchClear"
              aria-label={t('lsCancel')}
            >
              <CancelIcon width={14} height={14} />
            </button>
          )}
        </span>
      </Grid>
      <Grid item md={12} className={classes.w100}>
        {buildLocalSourcingExpandedGrid()}
      </Grid>
    </Grid>
  );

  const uploadReceiptPopupDiv = () => {
    const onFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files && event.target.files[0];
      if (event.target) event.target.value = '';
      if (!file) return;
      if (file.size > RECEIPT_MAX_BYTES) {
        setReceiptFile(null);
        setReceiptFileError(t('lsReceiptSizeLimit'));
        return;
      }
      setReceiptFileError('');
      setReceiptFile(file);
    };
    const formatBytes = (bytes: number) => {
      if (!bytes) return '0 KB';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };
    return (
      <Grid
        item
        md={12}
        className={`${classes.w100} ${classes.uploadReceiptBody} ${classes.uploadReceiptContainer}`}
      >
        <div className={classes.pendingActionsBanner}>
          {t('lsUploadReceiptBanner')}
        </div>

        <div className={classes.uploadReceiptBelowLabel}>
          {t('lsUploadReceiptBelow')}
        </div>

        <label
          htmlFor="ls-receipt-file-input"
          className={classes.receiptDropZone}
        >
          <div className={classes.receiptUploadIcon}>
            <CloudUploadIcon />
          </div>
          <div className={classes.receiptDropTitle}>
            {t('lsChooseFileDrag')}
          </div>
          <div className={classes.receiptDropHint}>
            {t('lsAcceptedFileTypes')}
          </div>
          <input
            id="ls-receipt-file-input"
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            className={classes.receiptFileInputHidden}
            onChange={onFileSelected}
          />
        </label>

        {receiptFileError && (
          <div role="alert" className={classes.receiptError}>
            {receiptFileError}
          </div>
        )}

        {receiptFile && (
          <div className={classes.receiptFileRow}>
            <div className={classes.receiptFileInfo}>
              <span className={classes.receiptFileTypeBadge}>PDF</span>
              <div>
                <div className={classes.receiptFileName}>
                  {receiptFile.name}
                </div>
                <div className={classes.receiptFileMeta}>
                  <span>
                    {formatBytes(receiptFile.size)} {t('lsSizeOf')}{' '}
                    {formatBytes(receiptFile.size)}
                  </span>
                  <span className={classes.receiptFileMetaDot} />
                  <span className={classes.receiptFileCompleted}>
                    <span
                      aria-hidden
                      className={classes.receiptFileCompletedIcon}
                    >
                      &#10003;
                    </span>
                    {t('lsCompleted')}
                  </span>
                </div>
              </div>
            </div>
            <span
              role="button"
              aria-label={t('lsRemoveFile')}
              data-testid="deleteIcon"
              className={classes.receiptDeleteButton}
              onClick={() => {
                setReceiptFile(null);
                setReceiptFileError('');
              }}
            >
              <DeleteRedIcon />
            </span>
          </div>
        )}

        <div className={classes.pendingActionsFooter}>
          <Btn
            variant="outlined"
            color="primary"
            disabled={uploadReceiptLoader}
            onClick={() => closeUploadReceiptPopup()}
          >
            {t('lsCancel')}
          </Btn>
          <Btn
            variant="contained"
            color="primary"
            disabled={!receiptFile || uploadReceiptLoader}
            onClick={() => submitReceipt()}
          >
            {t('lsSubmitReceipt')}
          </Btn>
        </div>
      </Grid>
    );
  };

  const receiptSuccessPopupDiv = () => (
    <Grid container>
      <Grid item md={12} className={classes.receiptSuccessBody}>
        <TickIcon />
        <Typography className={classes.receiptSuccessMessage}>
          {t('lsReceiptUploadSuccess')}
        </Typography>
        <Btn
          variant="contained"
          color="primary"
          onClick={() => closeReceiptSuccessPopup()}
          data-testid="localSourcingReceiptSuccessOk"
        >
          {t('lsOk')}
        </Btn>
      </Grid>
    </Grid>
  );

  const pendingActionsPopupDiv = () => {
    const actions = getLocalSourcingActions(selectedLocalSourcingTask || {});
    return (
      <Grid
        item
        md={12}
        className={`${classes.w100} ${classes.pendingActionsBody}`}
      >
        <div className={classes.pendingActionsBanner}>
          {t('lsPendingActionsBanner')}
        </div>

        <div className={classes.pendingActionsRadioList}>
          {actions.map((act) => {
            const selected = pendingActionsChoice === act;
            return (
              <label
                key={act}
                className={`${classes.pendingActionsRadioCard} ${
                  selected ? classes.pendingActionsRadioCardSelected : ''
                }`}
              >
                <input
                  type="radio"
                  name="ls-pending-action"
                  value={act}
                  checked={selected}
                  onChange={() => setPendingActionsChoice(act)}
                  className={classes.pendingActionsRadioInput}
                />
                <span>{t(getPendingActionPopupLabel(act))}</span>
              </label>
            );
          })}
        </div>

        <div className={classes.pendingActionsFooter}>
          <Btn
            variant="outlined"
            color="primary"
            onClick={() => closePendingActionsPopup()}
          >
            {t('lsCancel')}
          </Btn>
          <Btn
            variant="contained"
            color="primary"
            disabled={!pendingActionsChoice}
            onClick={() => proceedPendingAction()}
          >
            {t('lsProceed')}
          </Btn>
        </div>
      </Grid>
    );
  };

  const confirmActionPopupDiv = () => {
    if (!confirmActionOpen) return null;
    const content = CONFIRM_ACTION_CONTENT[confirmActionOpen];
    if (!content) return null;
    return (
      <Grid
        item
        md={12}
        className={`${classes.w100} ${classes.pendingActionsBody}`}
      >
        <div className={classes.pendingActionsBanner}>{t(content.banner)}</div>
        <div className={classes.pendingActionsFooter}>
          <Btn
            variant="outlined"
            color="primary"
            onClick={() => closeConfirmActionPopup()}
          >
            {t('lsCancel')}
          </Btn>
          <Btn
            variant="contained"
            color="primary"
            onClick={() => proceedConfirmAction()}
          >
            {t(content.cta)}
          </Btn>
        </div>
      </Grid>
    );
  };

  if (!enabled) return null;

  return (
    <>
      {uploadReceiptLoader && (
        <>
          <Grid className={classes.masterLoader} />
          <Grid className={classes.fullScreenLoader}>
            <CircularProgress />
          </Grid>
        </>
      )}
      {active && (
        <>
          {localSourcingLoader ? (
            <Grid
              className={`${classes.RACloaderNorecords} ${classes.textcenter} ${classes.w100}`}
            >
              <CircularProgress />
            </Grid>
          ) : localSourcingData.length === 0 ? (
            <Grid className={classes.textSpaceNorecords}>
              <Grid className={classes.textcenterNorecords}>
                <InfoIcon />
              </Grid>
              <Grid className={classes.textcenterImage}>
                {t('lsNoRecordsFound')}
              </Grid>
            </Grid>
          ) : (
            <Grid
              item
              md={12}
              id="localSourcingCompactScroll"
              className={`${classes.fixedheadertableonline} ${classes1.tabFlexWrap}`}
            >
              <InfiniteScroll
                next={fetchNextLocalSourcing}
                dataLength={localSourcingData.length}
                hasMore={localSourcingHasMore}
                scrollableTarget="localSourcingCompactScroll"
                className={classes.localSourcingInfiniteVisible}
                loader={
                  !localSourcingPop && localSourcingFetchLoader ? (
                    <Grid className={`${classes.textCenter} ${classes.w100}`}>
                      <CircularProgress size={20} />
                    </Grid>
                  ) : null
                }
              >
                {buildLocalSourcingGrid()}
              </InfiniteScroll>
            </Grid>
          )}

          {localSourcingData.length !== 0 ? (
            <Expandicon
              className={classes.RACexpandIcon}
              data-bs-toggle="modal"
              data-bs-target="#localSourcing"
              onClick={() => {
                if (localSourcingInFlightRef.current) {
                  setLocalSourcingPreloadOverlay(true);
                }
                setLocalSourcingPop(true);
              }}
              data-testid="expandLocalSourcingIcon"
              key="localSourcingPopupdata"
            />
          ) : null}
        </>
      )}

      <RACModalCard
        isOpen={localSourcingPop}
        maxWidth="xl"
        onClose={() => {
          setLocalSourcingSearchText('');
          setLocalSourcingPop(false);
        }}
        closeIcon={false}
        dialogOptionalProps={{
          open: localSourcingPop,
          disableBackdropClick: true,
          disableEscapeKeyDown: true,
        }}
        title={t('lsTitle')}
        TitleClassName="title"
        className={`${classes.localSourcingFullModal} title `}
        borderRadius="20px"
        key="localSourcingModalKeycard"
        data-testid="localSourcingTitleCard"
      >
        {localSourcingPopupDiv()}
      </RACModalCard>
      <RACModalCard
        borderRadius="20px"
        isOpen={uploadReceiptPopupOpen}
        maxWidth="xs"
        closeIcon={true}
        onClose={() => closeUploadReceiptPopup()}
        title={t('lsUploadReceiptTitle')}
        TitleClassName="title"
        className={classes.uploadReceiptModal}
      >
        {uploadReceiptPopupDiv()}
      </RACModalCard>
      <RACModalCard
        borderRadius="20px"
        isOpen={pendingActionsPopupOpen}
        maxWidth="xs"
        closeIcon={true}
        onClose={() => closePendingActionsPopup()}
        title={t('lsPendingActionsTitle')}
        TitleClassName="title"
        className={classes.pendingActionsModal}
      >
        {pendingActionsPopupDiv()}
      </RACModalCard>
      <RACModalCard
        borderRadius="20px"
        isOpen={!!confirmActionOpen}
        maxWidth="xs"
        closeIcon={true}
        onClose={() => closeConfirmActionPopup()}
        title={
          confirmActionOpen
            ? t(CONFIRM_ACTION_CONTENT[confirmActionOpen]?.title || '')
            : ''
        }
        TitleClassName="title"
        className={classes.pendingActionsModal}
      >
        {confirmActionPopupDiv()}
      </RACModalCard>
      <RACModalCard
        borderRadius="20px"
        isOpen={receiptSuccessOpen}
        maxWidth="xs"
        closeIcon={false}
        onClose={() => closeReceiptSuccessPopup()}
      >
        {receiptSuccessPopupDiv()}
      </RACModalCard>
    </>
  );
};

export default React.memo(LocalSourcingComponent);
